import axios from 'axios'

// Relative in every mode, so development takes the same routing as production.
// In dev, Vite's server.proxy (vite.config.mjs) forwards /api/v1 to the local
// API; in production the built bundle is served from the same origin as the API.
// This was previously an absolute http://localhost:8081 base URL in dev, which
// meant axios never emitted a relative path and the proxy config never fired —
// every dev request went cross-origin instead. Same-origin here removes the CORS
// preflight and makes the auth cookie first-party, so the SameSite=none/Secure
// path (and therefore local TLS) is not needed for ordinary development.
//
// To point development at a different backend, change the proxy target in
// vite.config.mjs rather than reintroducing an absolute base URL here.
const baseURL = '/api/v1';
const withCredentials = true;

let storeInstance = null;

// Dynamic import to avoid circular dependency - only loaded when needed
async function loadStore() {
  if (!storeInstance) {
    storeInstance = (await import('../services/store.js')).store
  }
  return storeInstance
}

export const api = axios.create({
    baseURL: baseURL,
    timeout: 60000,
    headers: {
        'Content-Type': 'application/json'
    },
    withCredentials: withCredentials
})

// Request interceptor
api.interceptors.request.use(async config => {

    const store = await loadStore();
    if (!config.skipLoading) {
        store.dispatch({ type: 'incrementRequests' });
    }
    
    // Add impersonation token if present
    const state = store.getState();
    if (state.impersonation && state.impersonation.token && config.skipImpersonation !== true) {
        config.headers.Authorization = `Bearer ${state.impersonation.token}`;
    }
    
    return config;
}, error => {
    return Promise.reject(error);
});

/**
 * METALARK-1129: the server sets X-Access-Session-Ended when it was handed a
 * workspace-access / impersonation token that is no longer valid. Drop the dead
 * token immediately so the next request goes out clean and the navbar stops
 * advertising a workspace the server has already stopped honoring.
 *
 * Checked on both success and error responses: a safe request falls back to the
 * session cookie and returns 200 with the header, while a write is refused with
 * 401 ACCESS_SESSION_ENDED and carries the same header.
 */
/**
 * Whether the API answering us is the mock server.
 *
 * Latched from the `X-MetaLark-Mock-Api` header the mock server sets on every response
 * (uis/front/mockserver/index.js). Starts false and only ever turns true, because the
 * safe default is "this is a real API": a page that assumes real data and gets mock data
 * shows something odd, while a page that assumes mock data and gets a real API shows a
 * canned fixture in place of the user's actual content — which is exactly the bug this
 * replaces. Reading `import.meta.env.MODE === 'development'` answered a different
 * question, and answered it wrong for every `npm run dev` session pointed at the local
 * stack.
 */
let mockApiDetected = false;

/** True once any response has identified itself as coming from the mock server. */
export function isMockApi() {
    return mockApiDetected;
}

function noteMockApiHeader(headers) {
    if (!headers) return;
    // Axios lower-cases response header names; guard both, as above.
    if (headers['x-metalark-mock-api'] || headers['X-MetaLark-Mock-Api']) {
        mockApiDetected = true;
    }
}

async function handleAccessSessionEndedHeader(headers) {
    if (!headers) return;
    // Axios lower-cases response header names; guard both for safety.
    const signal = headers['x-access-session-ended'] || headers['X-Access-Session-Ended'];
    if (!signal) return;

    const { clearStoredAccessSession } = await import('../services/access-session.js');
    await clearStoredAccessSession(`server-rejected:${signal}`);
}

// Response interceptor
api.interceptors.response.use(async response => {
    const store = await loadStore();
    if (!response.config.skipLoading) {
        store.dispatch({ type: 'decrementRequests' });
    }

    noteMockApiHeader(response.headers);
    await handleAccessSessionEndedHeader(response.headers);

    // Automatically mark session as active on successful auth responses
    // This covers ALL login paths: password, passkey, OAuth, conditional auth
    const authEndpoints = [
        '/auth/login',                        // Password login
        '/auth/passkey/login/verify',         // Passkey login
        '/auth/oauth/callback',               // OAuth callback
        '/auth/me'                            // OAuth popup flow calls this after login
    ];
    
    if (authEndpoints.some(endpoint => response.config.url.includes(endpoint))) {
        // Check if response indicates successful authentication (has user data)
        if (response.data && response.data.user) {
            sessionStorage.setItem('_hasActiveSession', 'true');
            console.log('✅ Session marked as active');
        }
    }
    
    return response;
}, async error => {
    const store = await loadStore();
    if (error.config && !error.config.skipLoading) {
        store.dispatch({ type: 'decrementRequests' });
    }

    // METALARK-1129: clear a rejected access-session token before any of the
    // status-specific handling below. The 401 branch re-checks /auth/me, and
    // that check only reflects reality once the dead Bearer is gone.
    // A 401 from the mock server is often the first response a page load sees, so the
    // error path has to latch this too or the flag arrives after the pages that read it.
    noteMockApiHeader(error.response?.headers);
    await handleAccessSessionEndedHeader(error.response?.headers);

    // Handle 402 Payment Required globally
    // This indicates a subscription issue (expired, no subscription, etc.)
    if (error.response && error.response.status === 402) {
        console.log('💳 402 Payment Required - subscription issue detected');

        // METALARK-802: only surface the modal when the billing UI is
        // actually reachable. The modal's "Go to Billing" sends the user
        // to /#billing, which is gated by the `billing_ui_page` feature
        // flag in routes.js — when that flag is off the route guard
        // redirects to /#home and the prompt becomes a dead end. The
        // navbar's billing link is hidden under the same flag, so this
        // keeps the two surfaces in sync.
        let billingUiEnabled = true;
        try {
            const { featureFlagService } = await import('./featureFlagService');
            billingUiEnabled = featureFlagService.isEnabledSync('billing_ui_page');
        } catch (flagErr) {
            console.warn('💳 Could not read billing_ui_page flag, defaulting to enabled', flagErr);
        }

        if (billingUiEnabled) {
            // Extract subscription details from error response
            const errorData = error.response.data || {};
            const details = errorData.details || {};

            const subscriptionError = {
                message: errorData.message || 'Active subscription required',
                details: {
                    requirementLevel: details.requirementLevel,
                    gracePeriodEnd: details.gracePeriodEnd,
                    daysRemaining: details.daysRemaining,
                    billingUrl: details.billingUrl || '/#billing',
                    blockedOperation: details.blockedOperation,
                    description: details.description
                }
            };

            // Dispatch subscription error to store for modal to pick up
            store.dispatch({ type: 'showSubscriptionError', payload: subscriptionError });
        } else {
            console.log('💳 billing_ui_page flag off — skipping subscription modal; caller handles 402 inline');
        }

        // Still reject the promise so calling code can handle if needed
        return Promise.reject(error);
    }

    
    // Handle 403 Forbidden - Check for EULA requirement
    if (error.response && error.response.status === 403) {
        const errorData = error.response.data;
        if (errorData && errorData.requiresEula) {
            console.log('🔒 EULA acceptance required, showing modal');
            // Trigger EULA modal via custom event
            window.dispatchEvent(new CustomEvent('eula-required', {
                detail: {
                    version: errorData.currentEulaVersion,
                    error: errorData.error,
                    message: errorData.message
                }
            }));
            // Mark error as intercepted to prevent duplicate handling
            return Promise.reject({
                ...error,
                eulaIntercepted: true
            });
        }
    }

    // Handle 401 Unauthorized globally
    // When we get a 401, verify if it's a real session expiration by checking /auth/me
    // This allows endpoints like /repositories/browse to return 401 with specific error codes
    // (e.g., oauth_token_expired) while still catching actual session expirations
    if (error.response && error.response.status === 401 && !error.config.url.includes('/auth/me')) {
        // Don't check /auth/me if this error IS from /auth/me (avoid infinite loop)
        console.log('📡 Received 401 - verifying session validity by calling /auth/me');
        
        try {
            // Make a request to check if session is actually valid
            await axios.get('/auth/me', {
                skipLoading: true,
                withCredentials: true,
                baseURL: baseURL,
                timeout: 5000 // Quick timeout for this check
            });
            
            // If /auth/me succeeds, the session IS valid
            // The 401 was from the specific endpoint (e.g., OAuth token expired for that service)
            // Return the response so it can be processed normally by the application
            console.log('✅ Session is valid (/auth/me succeeded) - treating as endpoint-specific error');
            // Return response with 401 status so caller can handle the error code
            return Promise.resolve(error.response);
        } catch (meCheckError) {
            // /auth/me also failed with 401 - session is truly expired
            if (meCheckError.response && meCheckError.response.status === 401) {
                console.log('🔒 401 Unauthorized on /auth/me - session truly expired, logging out');
                
                // Check if user had an active session (tracked in sessionStorage during login)
                const hadActiveSession = sessionStorage.getItem('_hasActiveSession') === 'true';
                
                if (hadActiveSession) {
                    // Clear the active session flag
                    sessionStorage.removeItem('_hasActiveSession');
                    
                    // Call logout endpoint to log the session end in audit trail
                    // This uses the grace period to allow logout even if token just expired
                    try {
                        await axios.post('/auth/tokens/logout', {}, {
                            withCredentials: true,
                            baseURL: baseURL,
                            skipLoading: true
                        });
                    } catch (logoutError) {
                        console.warn('⚠️ Could not log logout:', logoutError);
                        // Continue with redirect even if logout logging fails
                    }
                }
                
                // Clear session data
                try {
                    sessionStorage.clear();
                    localStorage.removeItem('authToken');
                    localStorage.removeItem('workspace_sliceId');
                    localStorage.removeItem('workspace_sliceName');
                    localStorage.removeItem('last_selected_slice_id');
                    console.log('✅ Session storage cleared');
                } catch (storageError) {
                    console.warn('⚠️ Could not clear session storage:', storageError);
                }
                
                // Clear Redux state (same as session-manager does)
                try {
                    store.dispatch({ type: 'clearImpersonation' });
                    store.dispatch({ type: 'setSliceId', payload: null });
                    store.dispatch({ type: 'CLEAR_CUSTOMER' });
                    
                    // Import tools dynamically to avoid circular dependency
                    const { tools } = await import('../services/tools.js');
                    await tools.clearUserContext();
                    console.log('✅ Redux state cleared');
                } catch (reduxError) {
                    console.error('❌ Failed to clear Redux state:', reduxError);
                }
                
                // Wait for Redux subscribers and Riot components to process state changes
                // This ensures navbar detects the cleared userContext before we navigate
                await new Promise(resolve => setTimeout(resolve, 200));

                // Redirect to login page. Only include reason=session-expired
                // when the user actually HAD a session that then expired —
                // otherwise (e.g. landing on the site unauthenticated, post
                // JWT-exchange cookie didn't stick) the banner would
                // incorrectly claim the user's session timed out.
                window.location.href = hadActiveSession
                    ? '#login?reason=session-expired'
                    : '#login';

                // Return a rejected promise to prevent further processing
                return Promise.reject(new Error(
                    hadActiveSession
                        ? 'Session expired - redirecting to login'
                        : 'Not authenticated - redirecting to login'
                ));
            } else {
                // /auth/me failed for a different reason (network, etc)
                // Let the original error pass through
                console.warn('⚠️ /auth/me check failed with different error:', meCheckError.message);
                return Promise.reject(error);
            }
        }
    }
    
    return Promise.reject(error);
});


